import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useAuthorizationsListQuery } from '~/queries/iam-authorizations'
import { keys as rolesKeys, useAddAuthsToRoleMutation } from '~/queries/user-roles'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { RequiredYupOption } from '~/utils/yup'

const MODAL_ID = 'add-auths-modal'

export interface Values {
  auths: IOption[]
}

const validationSchema = Yup.object().shape({
  auths: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED).min(1, FORM_ERRORS.AT_LEAST_ONE),
})

const initialValues: Values = {
  auths: [],
}

interface FormProps {
  onClose: VoidFunction
  onFailure: (e: unknown) => void
  onSuccess: VoidFunction
  roleAuthorizations: string[]
  roleId: string
}

const AddAuthsForm = ({ onClose, onFailure, onSuccess, roleAuthorizations, roleId }: FormProps): ReactElement => {
  /** Local state */

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })

  const queryClient = useQueryClient()

  /** Queries & Mutations */

  const { data: authorizations, isLoading: isAuthorizationsLoading } = useAuthorizationsListQuery()

  const { mutateAsync: addAuths } = useAddAuthsToRoleMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    const { auths } = values

    try {
      const authKeys = auths.map(({ value }) => value)
      await addAuths({ authKeys, roleId })
      await queryClient.invalidateQueries({ queryKey: rolesKeys.details(roleId) })
      onSuccess()
    } catch (e) {
      onFailure(e)
    }
  }

  /** Render */

  const authsOptions =
    authorizations?.data
      .filter(({ key }) => !roleAuthorizations.includes(key))
      .map(({ key }) => ({ label: key, value: key })) || []

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody>
        <RequiredInformation />
        <Controller
          control={control}
          name="auths"
          render={({ field }) => (
            <MultiSelectInput
              {...field}
              {...reactSelectMenuPositionDef(MODAL_ID)}
              error={errors.auths?.message}
              isLoading={isAuthorizationsLoading}
              label="Authorizations"
              options={authsOptions}
              placeholder="Select authorizations"
              required
            />
          )}
        />
      </ModalBody>
      <ModalFooter>
        <Button disabled={isSubmitting} ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Loading...' : 'Save'}
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const AddAuthsModal = ({
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  roleAuthorizations,
  roleId,
}: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add authorizations to the role</ModalHeader>
      <AddAuthsForm
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        roleAuthorizations={roleAuthorizations}
        roleId={roleId}
      />
    </Modal>
  )
}

export default AddAuthsModal
